import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Banner, Button, FormField, FormHelp, Heading, Input, Label, Modal, Text } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useRentalManagerUserPropertySummaryWebApiHandler_applications } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import ApplicationActions from '../../../actions/ApplicationActions';
import { PropertiesSelectors, RoutingSelectors } from '../../../selectors';

import KeyCodeConstants from '../../../constants/KeyCodeConstants';

import ListingActivationTerm from '../../ListingActivationTerms';

const HEADING_TEXT = 'Invite a potential tenant to apply';
const INVALID_EMAIL_MSG = 'please enter a valid email address';
const INVALID_NAME_MSG = 'please enter a valid name not exceeding 32 letters';

export const getDisplayName = (firstName, lastName, email) => {
  if (!firstName) {
    return email;
  }
  if (!lastName) {
    return firstName;
  }
  return `${firstName} ${lastName}`;
};

const InviteToApplyModal = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const currentProperty = useSelector((state) => PropertiesSelectors.makeCurrentProperty(state));
  const listingId = useSelector((state) => RoutingSelectors.makeListingId(state));

  // Form Fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [showFailureBanner, setShowFailureBanner] = useState(false);
  const [successBannerText, setSuccessBannerText] = useState('');

  // Form Field Errors
  const [errorObj, setErrorObj] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  const { street } = currentProperty;

  const isErrorFree = (() => {
    for (const key in errorObj) {
      if (errorObj[key] !== null && errorObj[key] !== '') {
        return false;
      }
    }
    return true;
  })();

  const isButtonEnabled = !!email && isErrorFree;
  const headingDisplay = street ? `${HEADING_TEXT} to ${street}` : HEADING_TEXT;

  const handleOnChange = (e, callback) => {
    // Clear banners
    if (showFailureBanner) {
      setShowFailureBanner(false);
    }

    if (showSuccessBanner) {
      setShowSuccessBanner(false);
    }

    const { value } = e.target;
    callback(value);
  };

  const handleSubmission = () => {
    // Clear Success Banner
    setShowSuccessBanner(false);
    setShowFailureBanner(false);

    const requestBody = {
      listingId,
      renterFirstName: firstName,
      renterLastName: lastName,
      toEmail: email,
    };
    setIsSubmitting(true);

    dispatch(ApplicationActions.inviteToApply(requestBody))
      .then((res) => {
        if (res.success) {
          /**
           * TODO: follow up if we need to invalidate queries here
           *
           * we are not enabling applications based on invite to apply
           * ex: queryClient.invalidateQueries({ queryKey: useRentalManagerUserPropertySummaryWebApiHandler_applications.getKey({ listingAlias }) });
           */
          setShowSuccessBanner(true);
          setSuccessBannerText(`You have invited ${getDisplayName(firstName, lastName, email)}
                        to apply to your rental.`);
          setFirstName('');
          setLastName('');
          setEmail('');

          /**
           * invalidate to accurately sync data
           *
           * ex: ApplicationsHeader: total number of applications
           *
           * TODO: listingId should be listingAlias
           */
          queryClient.invalidateQueries({
            queryKey: useRentalManagerUserPropertySummaryWebApiHandler_applications.getKey({ listingAlias: listingId }),
          });
        } else {
          setShowFailureBanner(true);
          logError({
            error: new Error(`Could not invite user to apply.`),
            errorType: 'InviteToApplyModalError',
            context: {
              listingId,
              details: 'InviteToApplyModal.handleSubmission',
              response: res,
            },
          });
        }
      })
      .catch((err) => {
        logError({
          error: err,
          context: {
            details: 'InviteToApplyModal.handleSubmission',
            message: 'Could not invite user to apply.',
            listingId,
          },
        });

        setShowFailureBanner(true);
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleKeyDown = (e) => {
    const { keyCode } = e;

    // if enter is pressed
    if (keyCode === KeyCodeConstants.ENTER) {
      handleSubmission();
    }
  };

  const validateFirstOrLastName = (name) => {
    const isValid = !name || name.length < 33;
    return isValid ? null : INVALID_NAME_MSG;
  };

  const handleFirstNameChange = () => {
    const errorMessage = validateFirstOrLastName(firstName);

    setErrorObj({
      ...errorObj,
      firstName: errorMessage,
    });
  };

  const handleLastNameChange = () => {
    const errorMessage = validateFirstOrLastName(lastName);

    setErrorObj({
      ...errorObj,
      lastName: errorMessage,
    });
  };

  const handleEmailChange = () => {
    const isValidEmail = /(.+?)@(.+?)\.[a-zA-Z]{1,}/.test(email);
    const errorMessage = isValidEmail ? null : INVALID_EMAIL_MSG;

    setErrorObj({
      ...errorObj,
      email: errorMessage,
    });
  };

  return (
    <Modal
      multiModalTriggerId="InviteToApplyModal"
      header={
        <Heading level={2} textStyle="heading-xs">
          Send application link
        </Heading>
      }
      body={
        <>
          {showSuccessBanner && (
            <Banner tone="success" className="fs-mask">
              {successBannerText}
            </Banner>
          )}

          {showFailureBanner && (
            <Banner tone="critical" className="fs-mask">
              There was an error inviting {getDisplayName(firstName, lastName, email)} to apply to your rental
            </Banner>
          )}

          <Text textStyle="heading-sm" asChild css={{ marginBottom: '400' }}>
            <p>{headingDisplay}</p>
          </Text>

          <FormField css={{ marginBlock: '300' }}>
            <Label required={true}>Email</Label>

            <Input
              aria-label="Email"
              onBlur={handleEmailChange}
              onChange={(e) => {
                handleOnChange(e, setEmail);
              }}
              value={email}
              onKeyDown={handleKeyDown}
              className="fs-mask"
            />

            <FormHelp error={!!errorObj.email}>{errorObj.email}</FormHelp>
          </FormField>

          <FormField css={{ marginBlock: '300' }}>
            <Label>First name</Label>

            <Input
              aria-label="First name"
              onBlur={handleFirstNameChange}
              onChange={(e) => {
                handleOnChange(e, setFirstName);
              }}
              value={firstName}
              className="fs-mask"
            />

            <FormHelp error={!!errorObj.firstName}>{errorObj.firstName}</FormHelp>
          </FormField>

          <FormField css={{ marginBlock: '300' }}>
            <Label>Last name</Label>

            <Input
              aria-label="Last name"
              onBlur={handleLastNameChange}
              onChange={(e) => {
                handleOnChange(e, setLastName);
              }}
              value={lastName}
              className="fs-mask"
            />

            <FormHelp error={!!errorObj.lastName}>{errorObj.lastName}</FormHelp>
          </FormField>

          <Button
            tone="brand"
            emphasis="filled"
            onClick={handleSubmission}
            disabled={!isButtonEnabled}
            loading={isSubmitting}
            fluid
            css={{ marginBlock: '200' }}
          >
            Send
          </Button>

          <ListingActivationTerm lowlight={true} actionText="By clicking Send" />
        </>
      }
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default InviteToApplyModal;
